import React, { useState, useEffect } from 'react';
import { useServicers } from '../components/servicers/ServicerFields';
import { canViewServicer } from '../lib/assetPermissions';
import { GeographicFilter, useGeographicFilter } from '../components/geografia/GeographicFilter';
import { GeographicBadge } from '../components/geografia/GeographicBadge';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Filter, Building2, LayoutGrid, LayoutList,
  Loader2, X, RefreshCw, ChevronDown, ChevronLeft, ChevronRight, CheckSquare,
  Star, Save, Trash2, Rocket, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { AssetExportDialog } from '../components/AssetExportDialog';
import BulkAssignModal from '../components/BulkAssignModal';
import { AssetAssignmentCell, assignedNames } from '../components/AssetAssignmentCell';
import RequestReviewModal from '../components/RequestReviewModal';
import MediacionAssignModal from '../components/MediacionAssignModal';
import ConfirmMediacionActivaModal from '../components/ConfirmMediacionActivaModal';
import ClosureReasonModal from '../components/ClosureReasonModal';
import CatastroBatchModal from '../components/CatastroBatchModal';
import {
  RoiValue,
  hasRealRoiVenta,
  getRoiVentaValue,
  hasRealRoiAlquiler,
  getRoiAlquilerValue,
} from '../components/RoiDisplay';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SEMAFORO_COLORS = {
  verde: 'bg-emerald-500',
  amarillo: 'bg-amber-400',
  naranja: 'bg-orange-500',
  rojo: 'bg-red-500',
};

const ACCION_BADGES = {
  PRIORIDAD_ALTA: 'bg-emerald-100 text-emerald-700',
  REVISAR: 'bg-amber-100 text-amber-700',
  STANDBY: 'bg-orange-100 text-orange-700',
  DESCARTAR: 'bg-red-100 text-red-700',
};

const ESTADO_OPTIONS = [
  { v: 'activo', l: 'Activo' },
  { v: 'standby', l: 'Standby' },
  { v: 'cerrado', l: 'Cerrado' },
  { v: 'papelera', l: 'Papelera' },
];

// Tooltips descriptivos (spec Feb 2026 §2 y §3).
const TOOLTIP_ESTADO = 'Situación de la oportunidad dentro de XÉNICOS';
const TOOLTIP_ETAPA = 'Punto del proceso de adquisición en el que se encuentra';

// Etapa visual: derived (read-only) from (estado, fase). Used for the main
// list view to give a clean pipeline overview.
const ETAPA_VISUAL_LABELS = {
  nuevo:          'Nuevo',
  estudio:        'Estudio',
  mediacion:      'Mediación',
  oferta:         'Oferta',
  proceso_compra: 'Proceso compra',
  adquirido:      'Adquirido',
};
const ETAPA_VISUAL_OPTIONS = Object.keys(ETAPA_VISUAL_LABELS);
const ETAPA_VISUAL_BADGES = {
  nuevo:          'bg-sky-100 text-sky-700 border-sky-200',
  estudio:        'bg-amber-100 text-amber-700 border-amber-200',
  mediacion:      'bg-violet-100 text-violet-700 border-violet-200',
  oferta:         'bg-blue-100 text-blue-700 border-blue-200',
  proceso_compra: 'bg-orange-100 text-orange-700 border-orange-200',
  adquirido:      'bg-emerald-100 text-emerald-700 border-emerald-200',
};

// Columnas opcionales en la tabla. El usuario puede activarlas/desactivarlas
// desde el "Selector de columnas" sobre el listado.
// `default` indica si la columna se muestra por defecto la primera vez.
const COLUMN_DEFS = [
  { key: 'npl',             label: 'NPL',                  default: true,  fixed: true,  width: 80 },
  { key: 'servicer',        label: 'SERVICER',             default: true,                width: 120 },
  { key: 'referencia',      label: 'Referencia catastral', default: true,                width: 110 },
  { key: 'direccion',       label: 'Dirección',            default: true,                width: 130 },
  { key: 'municipio',       label: 'Municipio',            default: true,                width: 95 },
  { key: 'tipo_op',         label: 'Tipo operativo',       default: true,                width: 85 },
  { key: 'precio',          label: 'Precio compra',        default: true,                width: 85 },
  { key: 'valor',           label: 'Valor salida',         default: true,                width: 90 },
  { key: 'economico',       label: 'Score económico',      default: true,                width: 80 },
  { key: 'roi_alquiler',    label: 'ROI Alquiler',         default: false,               width: 90 },
  { key: 'accion',          label: 'Acción recomendada',   default: true,                width: 90 },
  { key: 'etapa_visual',    label: 'Fase',                 default: true,                width: 110 },
  { key: 'estado',          label: 'Estado',               default: false,               width: 80 },
  { key: 'fase_judicial',   label: 'Fase judicial',        default: true,                width: 130 },
  { key: 'prioridad',       label: 'Prioridad',            default: true,                width: 95 },
  { key: 'mediador',        label: 'Mediador',             default: true,                width: 140 },
  { key: 'gestor',          label: 'Gestor',               default: false,               width: 140 },
  { key: 'inversor',        label: 'Inversor',             default: false,               width: 140 },
  { key: 'ocupacion',       label: 'Ocupación',            default: false,               width: 90 },
  { key: 'proveedor',       label: 'Proveedor',            default: false,               width: 110 },
  { key: 'provincia',       label: 'Provincia',            default: false,               width: 90 },
  { key: 'tipo_activo',     label: 'Tipo inmueble',        default: false,               width: 90 },
  { key: 'metros',          label: 'm²',                   default: false,               width: 60 },
  { key: 'updated_at',      label: 'Última actualización', default: false,               width: 130 },
];
const DEFAULT_COLUMNS = COLUMN_DEFS.filter(c => c.default).map(c => c.key);

const FASE_LABELS = {
  nuevo: 'Nuevo',
  revision: 'Revisión',
  analisis: 'Análisis',
  mediacion: 'Mediación',
  validacion: 'Validación',
  cierre: 'Cierre',
};
const FASE_OPTIONS = Object.keys(FASE_LABELS);

const FASE_JUDICIAL_LABELS = {
  sin_iniciar: 'Sin iniciar',
  demanda_presentada: 'Demanda presentada',
  auto_admision: 'Auto admisión',
  subasta_solicitada: 'Subasta solicitada',
  subasta_convocada: 'Subasta convocada',
  subasta_en_curso: 'Subasta en curso',
  subasta_celebrada: 'Subasta celebrada',
  auto_adjudicacion: 'Auto de adjudicación',
  adjudicacion: 'Adjudicación',
  lanzamiento: 'Lanzamiento',
  oposicion: 'Oposición',
  apelacion: 'Apelación',
  cerrado: 'Cerrado',
};
const FASE_JUDICIAL_OPTIONS = Object.keys(FASE_JUDICIAL_LABELS);

const ACCION_OPTIONS = ['PRIORIDAD_ALTA', 'REVISAR', 'STANDBY', 'DESCARTAR'];

const DESCARTE_MOTIVOS = [
  { v: 'FUERA_ZONA', l: 'Fuera de zona operativa' },
  { v: 'POCA_LIQUIDEZ', l: 'Ubicación con poca liquidez' },
  { v: 'DESCARTADO_PREVIAMENTE', l: 'Descartado previamente' },
  { v: 'NO_INMUEBLE_LOCAL', l: 'No inmueble / local' },
  { v: 'NO_RENTABLE', l: 'No rentable' },
  { v: 'ACTIVO_DESACTUALIZADO', l: 'Activo desactualizado (revisar estado judicial)' },
  { v: 'ACTIVO_QUEMADO', l: 'Activo quemado / ya trabajado' },
  { v: 'INFO_INSUFICIENTE', l: 'Información insuficiente' },
  { v: 'OCUPACION_CRITICA', l: 'Problema de ocupación crítico' },
  { v: 'ADQUIRIDA_POR_TERCERO', l: 'Adquirida por tercero' },
];

// Es un origen automático de cierre, no un motivo que se pueda asignar manualmente.
const CLOSURE_FILTER_OPTIONS = [
  ...DESCARTE_MOTIVOS,
  { v: 'AUSENTE_PERIMETRO', l: 'Ausente en Perímetro' },
];

// Lookup for fast label resolution + short labels for the listing chip
const DESCARTE_LABEL = Object.fromEntries(DESCARTE_MOTIVOS.map(m => [m.v, m.l]));
const DESCARTE_SHORT = {
  FUERA_ZONA: 'Fuera zona',
  POCA_LIQUIDEZ: 'Poca liquidez',
  DESCARTADO_PREVIAMENTE: 'Descart. previo',
  NO_INMUEBLE_LOCAL: 'No inmueble',
  NO_RENTABLE: 'No rentable',
  ACTIVO_DESACTUALIZADO: 'Desactualizado',
  ACTIVO_QUEMADO: 'Quemado',
  INFO_INSUFICIENTE: 'Info insuf.',
  OCUPACION_CRITICA: 'Ocup. crítica',
  DUPLICADO_FUSIONADO: 'Duplicado',
};

const AssetsPage = () => {
  const { user } = useAuth();
  const userRole = user?.role;
  const isMediador = userRole === 'mediador';
  // Mediador: al pulsar Estado/Fase abrimos un modal que sugiere solicitar
  // revisión al Gestor/Admin (spec Feb 2026).
  const [mediadorRoAsset, setMediadorRoAsset] = useState(null);
  const [mediadorRoField, setMediadorRoField] = useState(null);
  // Modal de asignación obligatoria al mover un activo a fase Mediación.
  const [mediacionAsset, setMediacionAsset] = useState(null);
  // (Feb 2026 mediacion_activa) Popup ¿Mantener en mediación al pasar a
  // Oferta/Proceso compra? Guarda { asset, value }.
  const [confirmMediacionActiva, setConfirmMediacionActiva] = useState(null);
  // (Feb 2026) Modal por-fila "Motivo de cierre" — independiente del flujo
  // bulk (usa el mismo componente pero con selectedIds acotado a 1 asset).
  const [individualClosureId, setIndividualClosureId] = useState(null);
  // (Feb 2026) Modal por-fila para cambiar los mediadores asignados desde
  // la columna "Mediador" de la tabla. Guarda el asset completo.
  const [mediadoresEditAsset, setMediadoresEditAsset] = useState(null);
  const [mediadoresDraft, setMediadoresDraft] = useState([]);
  const [showBulkReview, setShowBulkReview] = useState(false);
  const isReadOnly = ['mediador', 'inversor'].includes((user?.role || '').toLowerCase());
  const isSuperadmin = (user?.role || '').toString().toLowerCase() === 'superadmin';
  // Mediador/inversor no deben ver el `proveedor` (info confidencial del dueño
  // original de la deuda). Filtramos la columna del picker y de la tabla.
  const hideProveedor = isReadOnly;
  const canSeeServicer = canViewServicer(user);
  const availableColumnDefs = React.useMemo(
    () => COLUMN_DEFS.filter(c => (!hideProveedor || c.key !== 'proveedor') && (canSeeServicer || c.key !== 'servicer')),
    [hideProveedor, canSeeServicer],
  );
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showBulkReassignModal, setShowBulkReassignModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [geoFilter, setGeoFilter] = useGeographicFilter(user?.id, searchParams.get('exclusion_geografica'));
  const [servicers] = useServicers();
  const servicerNames = React.useMemo(() => new Map(servicers.map(s => [s.id, s.nombre])), [servicers]);
  const [storedServicerFilter, setServicerFilter] = useState(() => canSeeServicer ? (searchParams.get('servicer_id') || sessionStorage.getItem(`xen-servicer-${user?.id}`) || '') : '');
  const servicerFilter = canSeeServicer ? storedServicerFilter : '';
  useEffect(() => {
    if (canSeeServicer) sessionStorage.setItem(`xen-servicer-${user?.id}`, servicerFilter);
    else { sessionStorage.removeItem(`xen-servicer-${user?.id}`); setServicerFilter(''); }
  }, [canSeeServicer, servicerFilter, user?.id]);
  const [exporting, setExporting] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const lastRequest = React.useRef(0);
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-indexed
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [showFilters, setShowFilters] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState(() => { const v = searchParams.get('estado'); return v ? v.split(',') : []; });
  const [fase, setFase] = useState(() => { const v = searchParams.get('fase'); return v ? v.split(',') : []; });
  const [prioridad, setPrioridad] = useState(() => { const v = searchParams.get('prioridad'); return v ? v.split(',') : []; });
  const [semaforo, setSemaforo] = useState(() => { const v = searchParams.get('semaforo'); return v ? v.split(',') : []; });
  const [accion, setAccion] = useState(() => { const v = searchParams.get('accion_recomendada'); return v ? v.split(',') : []; });
  const [tipoOperativo, setTipoOperativo] = useState(() => { const v = searchParams.get('tipo_activo_operativo'); return v ? v.split(',') : []; });
  const [tipoInmueble, setTipoInmueble] = useState(() => { const v = searchParams.get('tipo_activo'); return v ? v.split(',') : []; });
  const [posesion, setPosesion] = useState(() => { const v = searchParams.get('posesion'); return v ? v.split(',') : []; });
  const [provincia, setProvincia] = useState(() => { const v = searchParams.get('provincia'); return v ? v.split(',') : []; });
  const [municipio, setMunicipio] = useState(() => { const v = searchParams.get('municipio'); return v ? v.split(',') : []; });
  const [proveedor, setProveedor] = useState(() => {
    // Mediador/inversor nunca filtran por proveedor: ignoramos cualquier valor
    // que pudiera venir en la URL (ej. enlace copiado por otro rol).
    if (['mediador', 'inversor'].includes((user?.role || '').toLowerCase())) return [];
    const v = searchParams.get('proveedor'); return v ? v.split(',') : [];
  });
  const [motivoDescarte, setMotivoDescarte] = useState(() => { const v = searchParams.get('motivo_descarte'); return v ? v.split(',') : []; });
  const [faseJudicial, setFaseJudicial] = useState(() => { const v = searchParams.get('fase_judicial'); return v ? v.split(',') : []; });
  const [etapaVisual, setEtapaVisual] = useState(() => { const v = searchParams.get('etapa_visual'); return v ? v.split(',') : []; });
  const [sortBy, setSortBy] = useState('score_premediacion');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sinM2, setSinM2] = useState(() => searchParams.get('sin_metros') === 'true');
  const [esTop, setEsTop] = useState(() => searchParams.get('es_top') === 'true');
  const [pendienteRevision, setPendienteRevision] = useState(() => searchParams.get('pendiente_revision') === 'true');
  const [esNuevoImport, setEsNuevoImport] = useState(() => searchParams.get('es_nuevo_import') === 'true');
  const [enDescuento, setEnDescuento] = useState(() => searchParams.get('en_descuento') === 'true');
  // "Usuario asignado" (solo admin/superadmin): filtra activos donde el user
  // seleccionado aparece como gestor/mediador/inversor. Un único usuario a la vez.
  const isAdminOrSuper = ['admin', 'superadmin'].includes((user?.role || '').toString().toLowerCase());
  const [assignedUser, setAssignedUser] = useState(() => (isAdminOrSuper ? (searchParams.get('assigned_user') || '') : ''));
  const [usersList, setUsersList] = useState([]);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkEstado, setBulkEstado] = useState('');
  const [bulkPrioridad, setBulkPrioridad] = useState('');
  // Optional shared note: se añade a la pestaña Notas de cada activo alterado
  // por una acción masiva (estado, prioridad, asignación, descarte).
  const [bulkNota, setBulkNota] = useState('');
  const [applyingBulk, setApplyingBulk] = useState(false);

  // Visible columns (persisted in localStorage). The picker lives in a
  // dropdown above the table so users can toggle columns on the fly.
  // Migration (Feb 2026): when we add a NEW column marked `default: true`
  // (e.g. "mediador"), existing users would keep their old saved list and
  // never see it. We track separately which column keys the user has
  // already been exposed to (`assets-cols-known`). Any new default column
  // absent from that set is auto-added to their `visibleColumns` on load
  // — one-time, non-destructive; user choices for other columns persist.
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const allKeys = COLUMN_DEFS.map(c => c.key);
      const saved = JSON.parse(localStorage.getItem('assets-cols') || 'null');
      if (Array.isArray(saved) && saved.length) {
        const knownRaw = JSON.parse(localStorage.getItem('assets-cols-known') || '[]');
        const knownSet = new Set(Array.isArray(knownRaw) ? knownRaw : []);
        const newDefaultsMissing = COLUMN_DEFS
          .filter(c => c.default && !knownSet.has(c.key) && !saved.includes(c.key))
          .map(c => c.key);
        localStorage.setItem('assets-cols-known', JSON.stringify(allKeys));
        return newDefaultsMissing.length ? [...saved, ...newDefaultsMissing] : saved;
      }
      localStorage.setItem('assets-cols-known', JSON.stringify(allKeys));
    } catch { /* ignore */ }
    return DEFAULT_COLUMNS;
  });
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = React.useRef(null);
  React.useEffect(() => {
    const onClick = (e) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setShowColPicker(false);
    };
    if (showColPicker) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showColPicker]);
  React.useEffect(() => {
    try { localStorage.setItem('assets-cols', JSON.stringify(visibleColumns)); } catch { /* ignore */ }
  }, [visibleColumns]);
  const toggleColumn = (key) => {
    setVisibleColumns(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const colVisible = (key) => visibleColumns.includes(key);

  // Inline-edit (popover) — lets the user change estado / fase / fase_judicial /
  // prioridad / asignación directly from the listing without entering the edit page.
  const [inlineEdit, setInlineEdit] = useState(null); // { id, field, value }
  const [savingInline, setSavingInline] = useState(false);
  const openInline = (a, field) => setInlineEdit({ id: a.id, field, value: a[field] || '' });
  const closeInline = () => setInlineEdit(null);
  const saveInline = async () => {
    if (!inlineEdit) return;
    setSavingInline(true);
    try {
      await axios.patch(`${API}/assets/${inlineEdit.id}/quick-update`,
        { [inlineEdit.field]: inlineEdit.value || null },
        { withCredentials: true });
      toast.success('Cambio guardado');
      setInlineEdit(null);
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSavingInline(false); }
  };
  const [enrichingSel, setEnrichingSel] = useState(false);
  const [catastroBatch, setCatastroBatch] = useState(null);
  const [recalcSel, setRecalcSel] = useState(false);
  // Descarte modal state (asked when estado=descartado is applied)
  const [showDescarteModal, setShowDescarteModal] = useState(false);
  const [descarteMotivos, setDescarteMotivos] = useState([]);

  // Saved filters ("Mis filtros" / favoritos)
  const [savedFilters, setSavedFilters] = useState([]);
  const [savedFiltersLoaded, setSavedFiltersLoaded] = useState(false);
  const [activeSavedFilterId, setActiveSavedFilterId] = useState(null);
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [newFilterIsDefault, setNewFilterIsDefault] = useState(false);
  const [savingFilter, setSavingFilter] = useState(false);

  useEffect(() => {
    if (estado.length || motivoDescarte.length || fase.length || etapaVisual.length || prioridad.length || semaforo.length || accion.length || tipoOperativo.length || tipoInmueble.length || posesion.length || provincia.length || municipio.length || proveedor.length || sinM2 || esTop) {
      setShowFilters(true);
    }
  }, []);

  useEffect(() => { fetchFilters(); }, []);

  // Cargar lista de usuarios: la usa (a) el filtro "Usuario asignado"
  // (admin/superadmin) y (b) la columna "Mediador" editable para el resto
  // de roles editores. El endpoint GET /users está abierto a
  // superadmin/admin/gestor.
  useEffect(() => {
    const canFetchUsers = ['superadmin', 'admin', 'gestor'].includes((user?.role || '').toString().toLowerCase());
    if (!canFetchUsers) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/users`, { withCredentials: true });
        setUsersList(r.data.items ?? r.data ?? []);
      } catch { /* noop */ }
    })();
  }, [user?.role]);

  // Load saved filters once. If no URL params were set AND the user has a
  // `is_default` favorite, apply it automatically on first mount so the view
  // opens with the user's preferred configuration.
  useEffect(() => {
    const loadSaved = async () => {
      try {
        const r = await axios.get(`${API}/saved-filters`, { withCredentials: true });
        const list = (r.data.items ?? r.data) || [];
        setSavedFilters(list);
        const hasUrlParams = Array.from(searchParams.keys()).length > 0;
        if (!hasUrlParams) {
          const def = list.find(f => f.is_default);
          if (def) applySavedFilter(def);
        }
      } catch { /* noop */ }
      finally { setSavedFiltersLoaded(true); }
    };
    loadSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setPage(0); }, [search, provincia, municipio, proveedor, estado, fase, etapaVisual, prioridad, semaforo, accion, tipoOperativo, tipoInmueble, posesion, sortBy, sortOrder, sinM2, esTop, esNuevoImport, enDescuento, assignedUser, geoFilter, servicerFilter, pageSize]);

  // Sync current filter state to the URL (replace — no extra history entry).
  // Keeps filters preserved when the user clicks an asset and navigates back
  // via the browser back button or the detail page's back arrow.
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (provincia.length) params.set('provincia', provincia.join(','));
    if (municipio.length) params.set('municipio', municipio.join(','));
    if (proveedor.length) params.set('proveedor', proveedor.join(','));
    if (estado.length) params.set('estado', estado.join(','));
    if (motivoDescarte.length) params.set('motivo_descarte', motivoDescarte.join(','));
    if (faseJudicial.length) params.set('fase_judicial', faseJudicial.join(','));
    if (fase.length) params.set('fase', fase.join(','));
    if (etapaVisual.length) params.set('etapa_visual', etapaVisual.join(','));
    if (prioridad.length) params.set('prioridad', prioridad.join(','));
    if (semaforo.length) params.set('semaforo', semaforo.join(','));
    if (accion.length) params.set('accion_recomendada', accion.join(','));
    if (tipoOperativo.length) params.set('tipo_activo_operativo', tipoOperativo.join(','));
    if (tipoInmueble.length) params.set('tipo_activo', tipoInmueble.join(','));
    if (posesion.length) params.set('posesion', posesion.join(','));
    if (sinM2) params.set('sin_metros', 'true');
    if (esTop) params.set('es_top', 'true');
    if (pendienteRevision) params.set('pendiente_revision', 'true');
    if (esNuevoImport) params.set('es_nuevo_import', 'true');
    if (enDescuento) params.set('en_descuento', 'true');
    if (assignedUser) params.set('assigned_user', assignedUser);
    params.set('exclusion_geografica', geoFilter);
    if (servicerFilter) params.set('servicer_id', servicerFilter);
    setSearchParams(params, { replace: true });
  }, [search, provincia, municipio, proveedor, estado, motivoDescarte, faseJudicial, fase, etapaVisual, prioridad, semaforo, accion, tipoOperativo, tipoInmueble, posesion, sinM2, esTop, pendienteRevision, esNuevoImport, enDescuento, assignedUser, geoFilter, servicerFilter, setSearchParams]);

  useEffect(() => {
    const timer = setTimeout(() => fetchAssets(), 300);
    return () => clearTimeout(timer);
  }, [search, provincia, municipio, proveedor, estado, motivoDescarte, faseJudicial, fase, etapaVisual, prioridad, semaforo, accion, tipoOperativo, tipoInmueble, posesion, sortBy, sortOrder, sinM2, esTop, pendienteRevision, esNuevoImport, enDescuento, assignedUser, geoFilter, servicerFilter, page, pageSize]);

  const fetchFilters = async () => {
    try { const r = await axios.get(`${API}/filters`, { withCredentials: true }); setFilters(r.data); } catch { /* noop */ }
  };

  const fetchAssets = async () => {
    const requestId = ++lastRequest.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (provincia.length) params.append('provincia', provincia.join(','));
      if (municipio.length) params.append('municipio', municipio.join(','));
      if (proveedor.length) params.append('proveedor', proveedor.join(','));
      if (estado.length) params.append('estado', estado.join(','));
      if (motivoDescarte.length) params.append('motivo_descarte', motivoDescarte.join(','));
      if (faseJudicial.length) params.append('fase_judicial', faseJudicial.join(','));
      if (fase.length) params.append('fase', fase.join(','));
      if (etapaVisual.length) params.append('etapa_visual', etapaVisual.join(','));
      if (prioridad.length) params.append('prioridad', prioridad.join(','));
      if (semaforo.length) params.append('semaforo', semaforo.join(','));
      if (accion.length) params.append('accion_recomendada', accion.join(','));
      if (tipoOperativo.length) params.append('tipo_activo_operativo', tipoOperativo.join(','));
      if (tipoInmueble.length) params.append('tipo_activo', tipoInmueble.join(','));
      if (posesion.length) params.append('posesion', posesion.join(','));
      if (sinM2) params.append('sin_metros', 'true');
      if (esTop) params.append('es_top', 'true');
      if (pendienteRevision) params.append('pendiente_revision', 'true');
      if (esNuevoImport) params.append('es_nuevo_import', 'true');
      if (enDescuento) params.append('en_descuento', 'true');
      if (assignedUser) params.append('assigned_user', assignedUser);
      params.append('exclusion_geografica', geoFilter);
      if (servicerFilter) params.append('servicer_id', servicerFilter);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      params.append('skip', String(page * pageSize));
      params.append('limit', String(pageSize));
      const r = await axios.get(`${API}/assets?${params.toString()}`, { withCredentials: true });
      if (requestId !== lastRequest.current) return;
      if (r.data.items !== undefined) {
        setAssets(r.data.items);
        setTotal(r.data.total ?? r.data.items.length);
      } else {
        setAssets(r.data);
        setTotal(r.data.length);
      }
    } catch { toast.error('Error al cargar activos'); }
    finally { if (requestId === lastRequest.current) setLoading(false); }
  };

  const exportFiltered = async (columns) => {
    setExporting(true);
    try {
      const params = new URLSearchParams(searchParams);
      params.delete('export_columns');
      params.delete('skip');
      params.delete('limit');
      if (isAdminOrSuper && Array.isArray(columns)) params.set('export_columns', columns.join(','));
      if (!canSeeServicer) params.delete('servicer_id');
      params.set('exclusion_geografica', geoFilter);
      params.set('sort_by', sortBy); params.set('sort_order', sortOrder); params.set('export_csv', 'true');
      const r = await axios.get(`${API}/assets?${params}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement('a'); link.href = url; link.download = 'xenicos-activos-filtrados.csv'; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return true;
    } catch { toast.error('No se pudo exportar la vista filtrada'); return false; }
    finally { setExporting(false); }
  };

  const recalculateAll = async () => {
    setRecalculating(true);
    try {
      const r = await axios.post(`${API}/assets/recalculate-scores`, {}, { withCredentials: true });
      toast.success(`${r.data.recalculated} activos recalculados`);
      fetchAssets();
    } catch { toast.error('Error'); }
    finally { setRecalculating(false); }
  };

  const clearFilters = () => {
    setSearch(''); setProvincia([]); setMunicipio([]); setProveedor([]);
    setEstado([]); setMotivoDescarte([]); setFaseJudicial([]); setFase([]); setEtapaVisual([]); setPrioridad([]); setSemaforo([]);
    setAccion([]); setTipoOperativo([]); setTipoInmueble([]); setPosesion([]);
    setSinM2(false);
    setEsTop(false);
    setPendienteRevision(false);
    setEsNuevoImport(false);
    setEnDescuento(false);
    setAssignedUser('');
    setGeoFilter('todos');
    setServicerFilter('');
    setSortBy('score_premediacion'); setSortOrder('desc');
    setSelectedIds([]);
  };

  const hasActiveFilters = provincia.length || municipio.length || proveedor.length || estado.length || motivoDescarte.length || fase.length || etapaVisual.length || prioridad.length || semaforo.length || accion.length || tipoOperativo.length || tipoInmueble.length || posesion.length || sinM2 || esTop || pendienteRevision || esNuevoImport || enDescuento || assignedUser || geoFilter !== 'todos' || servicerFilter;

  const formatCurrency = (v) => !v ? '-' : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

  const getEstadoBadge = (e) => ({ nuevo: 'badge-blue', revisar: 'badge-warning', activo: 'badge-success', en_proceso: 'badge-warning', standby: 'badge-neutral', cerrado: 'badge-neutral', descartado: 'badge-error', papelera: 'badge-neutral' }[e] || 'badge-neutral');

  const getPrioridadBadge = (p) => ({
    urgente: 'bg-red-100 text-red-700 border-red-200',
    alta: 'bg-orange-100 text-orange-700 border-orange-200',
    normal: 'bg-blue-50 text-blue-700 border-blue-200',
    baja: 'bg-slate-100 text-slate-600 border-slate-200',
    no_prioritario: 'bg-slate-50 text-slate-400 border-slate-200',
  }[p] || 'bg-slate-100 text-slate-600 border-slate-200');

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
    } catch { return iso; }
  };

  // Click-to-edit chip helper. Renders the chip; if it is the active inline-
  // edit target it morphs into a <select> that auto-saves on change.
  const InlineChip = ({ asset, field, options, currentValue, currentLabel, badgeClass, placeholder = '—', testid, disabled = false, disabledTitle, onDisabledClick, beforeSave }) => {
    const isEditing = !disabled && inlineEdit && inlineEdit.id === asset.id && inlineEdit.field === field;
    const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
    if (isEditing) {
      return (
        <select
          autoFocus
          value={inlineEdit.value || ''}
          disabled={savingInline}
          onClick={stop}
          onChange={(e) => {
            const v = e.target.value;
            setInlineEdit({ id: asset.id, field, value: v });
            // Save right away with the new value (avoid stale state)
            (async () => {
              // Interceptor opcional (ej. abrir modal de asignación de
              // mediador antes de mover a fase "mediacion").
              if (typeof beforeSave === 'function') {
                try {
                  const intercepted = await beforeSave(v, asset);
                  if (intercepted) { setInlineEdit(null); return; }
                } catch { /* fallback a save normal */ }
              }
              setSavingInline(true);
              try {
                await axios.patch(
                  `${API}/assets/${asset.id}/quick-update`,
                  { [field]: v || null },
                  { withCredentials: true }
                );
                toast.success('Cambio guardado');
                setInlineEdit(null);
                fetchAssets();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Error al guardar');
              } finally { setSavingInline(false); }
            })();
          }}
          onBlur={() => { if (!savingInline) setInlineEdit(null); }}
          className="text-xs rounded border px-1 py-0.5"
          style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)', minWidth: 100, maxWidth: 160 }}
          data-testid={`inline-select-${field}-${asset.id}`}
        >
          <option value="">— sin valor —</option>
          {options.map(o => (
            <option key={o.v} value={o.v}>{o.l}</option>
          ))}
        </select>
      );
    }
    const handleClick = (e) => {
      stop(e);
      if (disabled) {
        if (typeof onDisabledClick === 'function') onDisabledClick(asset, field);
        return;
      }
      openInline(asset, field);
    };
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled && !onDisabledClick}
        className={`text-xs px-2 py-0.5 rounded-full font-medium border transition-shadow ${disabled ? (onDisabledClick ? 'cursor-pointer hover:shadow-sm hover:opacity-90' : 'opacity-60 cursor-not-allowed') : 'hover:shadow-sm hover:opacity-90 cursor-pointer'} ${badgeClass || 'bg-slate-100 text-slate-600 border-slate-200'}`}
        title={disabled ? (disabledTitle || 'No editable') : 'Clic para editar'}
        data-testid={testid || `inline-chip-${field}-${asset.id}`}
      >
        {currentLabel || (currentValue ? String(currentValue).replace(/_/g, ' ') : placeholder)}
      </button>
    );
  };

  // ----- Bulk selection helpers -----
  const allPageIds = assets.map(a => a.id);
  const allPageSelected = allPageIds.length > 0 && allPageIds.every(id => selectedIds.includes(id));
  const togglePageSelection = () => {
    if (allPageSelected) setSelectedIds(prev => prev.filter(id => !allPageIds.includes(id)));
    else setSelectedIds(prev => Array.from(new Set([...prev, ...allPageIds])));
  };
  const toggleOne = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const clearSelection = () => setSelectedIds([]);

  const applyBulkEstado = async () => {
    if (!bulkEstado || selectedIds.length === 0) return;
    // Cerrado requires picking a motivo first → open modal
    if (bulkEstado === 'cerrado') {
      setDescarteMotivos([]);
      setShowDescarteModal(true);
      return;
    }
    if (!window.confirm(`¿Cambiar estado a "${bulkEstado}" en ${selectedIds.length} activos?`)) return;
    await doBulkEstadoUpdate(bulkEstado, []);
  };

  const doBulkEstadoUpdate = async (estadoValue, motivos) => {
    setApplyingBulk(true);
    try {
      const body = { ids: selectedIds, estado: estadoValue };
      if (estadoValue === 'cerrado' && motivos && motivos.length) {
        // Reutilizamos `descarte_motivos` (nombre interno) para el motivo de
        // cierre — spec Feb 2026. El backend genera automáticamente la nota
        // en la ficha usando el mismo pipeline anterior.
        body.descarte_motivos = motivos;
      }
      const trimmedNota = (bulkNota || '').trim();
      if (trimmedNota) body.nota = trimmedNota;
      const r = await axios.post(`${API}/assets/bulk-update-estado`, body, { withCredentials: true });
      toast.success(`${r.data.updated} activos actualizados${trimmedNota ? ' · nota añadida' : ''}`);
      setSelectedIds([]);
      setBulkEstado('');
      setBulkNota('');
      setShowDescarteModal(false);
      setDescarteMotivos([]);
      fetchAssets();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setApplyingBulk(false); }
  };

  const confirmDescarte = () => {
    if (!descarteMotivos.length) {
      toast.error('Selecciona al menos un motivo');
      return;
    }
    doBulkEstadoUpdate('cerrado', descarteMotivos);
  };

  const PRIORIDAD_LABEL = { baja: 'Baja', normal: 'Normal', alta: 'Alta', urgente: 'Urgente', no_prioritario: 'No prioritario' };

  const applyBulkPrioridad = async () => {
    if (!bulkPrioridad || selectedIds.length === 0) return;
    if (!window.confirm(`¿Cambiar prioridad a "${PRIORIDAD_LABEL[bulkPrioridad] || bulkPrioridad}" en ${selectedIds.length} activos?`)) return;
    setApplyingBulk(true);
    try {
      const trimmedNota = (bulkNota || '').trim();
      const body = { ids: selectedIds, prioridad: bulkPrioridad };
      if (trimmedNota) body.nota = trimmedNota;
      const r = await axios.post(`${API}/assets/bulk-update-prioridad`, body, { withCredentials: true });
      toast.success(`${r.data.updated} activos actualizados${trimmedNota ? ' · nota añadida' : ''}`);
      setSelectedIds([]);
      setBulkPrioridad('');
      setBulkNota('');
      fetchAssets();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setApplyingBulk(false); }
  };

  const toggleMotivo = (v) => {
    // Single motivo principal (radio behaviour). Storage stays as an array
    // for backward compatibility with existing data and APIs.
    setDescarteMotivos([v]);
  };

  const enrichSelectedCatastro = async () => {
    if (selectedIds.length === 0) return;
    const ids = [...selectedIds];
    if (!window.confirm(`¿SOLICITAR DATOS A CATASTRO para ${ids.length} activo${ids.length === 1 ? '' : 's'} seleccionado${ids.length === 1 ? '' : 's'}? (datos, m², latitud y longitud)`)) return;
    const CHUNK = 10;
    const acc = { completados: 0, parciales: 0, sin_referencia: 0, errores: 0 };
    let detalle = [];
    let rateLimited = false;
    setEnrichingSel(true);
    setCatastroBatch({ total: ids.length, done: 0, running: true, resumen: { ...acc }, detalle: [], rateLimited: false });
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      try {
        const r = await axios.post(`${API}/assets/enrich-catastro-selected`, { ids: chunk }, { withCredentials: true, timeout: 120000 });
        Object.keys(acc).forEach(k => { acc[k] += r.data.resumen?.[k] || 0; });
        detalle = detalle.concat(r.data.detalle || []);
        rateLimited = rateLimited || !!r.data.rate_limited;
      } catch (err) {
        // El lote continúa aunque falle un tramo.
        acc.errores += chunk.length;
        const msg = err.response?.data?.detail || err.message;
        detalle = detalle.concat(chunk.map(cid => ({ id: cid, resultado: 'error', mensaje: `Error en la petición: ${msg}` })));
      }
      setCatastroBatch({ total: ids.length, done: Math.min(i + CHUNK, ids.length), running: true, resumen: { ...acc }, detalle, rateLimited });
      if (rateLimited) {
        const rest = ids.slice(i + CHUNK);
        acc.errores += rest.length;
        detalle = detalle.concat(rest.map(cid => ({ id: cid, resultado: 'error', mensaje: 'No consultado: límite por hora de Catastro' })));
        break;
      }
    }
    setCatastroBatch({ total: ids.length, done: ids.length, running: false, resumen: { ...acc }, detalle, rateLimited });
    setEnrichingSel(false);
    setSelectedIds([]);
    fetchAssets();
  };

  const recalcSelectedScore = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`¿Recalcular Score Económico para ${selectedIds.length} activos?`)) return;
    setRecalcSel(true);
    try {
      const r = await axios.post(`${API}/assets/recalculate-scores-selected`, { ids: selectedIds }, { withCredentials: true, timeout: 120000 });
      toast.success(`${r.data.recalculated} activos con score actualizado`);
      setSelectedIds([]);
      fetchAssets();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setRecalcSel(false); }
  };

  const ScoreBar = ({ value, label }) => {
    if (value == null) return <span className="text-xs text-slate-300">-</span>;
    const color = value >= 80 ? 'bg-emerald-500' : value >= 60 ? 'bg-amber-400' : value >= 40 ? 'bg-orange-500' : 'bg-red-500';
    return (
      <div className="flex items-center gap-1.5" title={`${label}: ${value}`}>
        <div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
        </div>
        <span className="text-xs font-medium text-slate-600 w-6 text-right">{Math.round(value)}</span>
      </div>
    );
  };

  const SemaforoDot = ({ value }) => {
    if (!value) return <span className="text-xs text-slate-300">-</span>;
    return <div className={`w-3 h-3 rounded-full ${SEMAFORO_COLORS[value] || 'bg-slate-300'}`} title={value} data-testid="semaforo-dot" />;
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  // ----- Saved filters helpers -----
  const currentFilterSnapshot = () => ({
    search, provincia, municipio, proveedor,
    estado, motivoDescarte, fase, etapaVisual, prioridad, semaforo, accion,
    tipoOperativo, tipoInmueble, posesion, sinM2, esTop,
    sortBy, sortOrder, pageSize, geoFilter, ...(canSeeServicer ? { servicerFilter } : {}),
  });

  function applySavedFilter(f) {
    const v = f.filters || {};
    setSearch(v.search || '');
    setProvincia(v.provincia || []);
    setMunicipio(v.municipio || []);
    setProveedor(v.proveedor || []);
    setEstado(v.estado || []);
    setMotivoDescarte(v.motivoDescarte || []);
    setFase(v.fase || []);
    setEtapaVisual(v.etapaVisual || []);
    setPrioridad(v.prioridad || []);
    setSemaforo(v.semaforo || []);
    setAccion(v.accion || []);
    setTipoOperativo(v.tipoOperativo || []);
    setTipoInmueble(v.tipoInmueble || []);
    setPosesion(v.posesion || []);
    setSinM2(!!v.sinM2);
    setEsTop(!!v.esTop);
    setGeoFilter(v.geoFilter || 'todos');
    setServicerFilter(canSeeServicer ? (v.servicerFilter || '') : '');
    if (v.sortBy) setSortBy(v.sortBy);
    if (v.sortOrder) setSortOrder(v.sortOrder);
    if (v.pageSize) setPageSize(v.pageSize);
    setActiveSavedFilterId(f.id);
    setShowFilters(true);
  }

  const saveCurrentFilter = async () => {
    if (!newFilterName.trim()) {
      toast.error('Pon un nombre al filtro');
      return;
    }
    setSavingFilter(true);
    try {
      const body = {
        name: newFilterName.trim(),
        filters: currentFilterSnapshot(),
        is_default: newFilterIsDefault,
      };
      const r = await axios.post(`${API}/saved-filters`, body, { withCredentials: true });
      // Refresh list (backend handles default-uniqueness)
      const list = await axios.get(`${API}/saved-filters`, { withCredentials: true });
      setSavedFilters((list.data.items ?? list.data) || []);
      setActiveSavedFilterId(r.data.id);
      setShowSaveFilterModal(false);
      setNewFilterName('');
      setNewFilterIsDefault(false);
      toast.success('Filtro guardado');
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setSavingFilter(false); }
  };

  const deleteSavedFilter = async (fid) => {
    if (!window.confirm('¿Eliminar este filtro guardado?')) return;
    try {
      await axios.delete(`${API}/saved-filters/${fid}`, { withCredentials: true });
      setSavedFilters(prev => prev.filter(f => f.id !== fid));
      if (activeSavedFilterId === fid) setActiveSavedFilterId(null);
      toast.success('Filtro eliminado');
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const toggleDefaultSavedFilter = async (fid) => {
    try {
      const r = await axios.post(`${API}/saved-filters/${fid}/set-default`, {}, { withCredentials: true });
      const nowDefault = r.data?.is_default;
      // Al marcar → solo ese filtro queda como default. Al desmarcar →
      // ninguno. Se actualiza el estado local con esas reglas.
      setSavedFilters(prev => prev.map(f => ({
        ...f,
        is_default: nowDefault ? f.id === fid : (f.id === fid ? false : f.is_default && f.id !== fid ? false : f.is_default),
      })));
      toast.success(nowDefault ? 'Marcado como predeterminado' : 'Filtro predeterminado desmarcado');
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="assets-page">
      {showExportDialog && isAdminOrSuper && <AssetExportDialog onClose={() => setShowExportDialog(false)} onExport={exportFiltered} />}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-1">Base de Activos</h1>
          <p className="body-text mt-1" data-testid="assets-total">{total} activos encontrados</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-secondary text-xs" data-testid="export-filtered-assets" disabled={exporting || loading} onClick={() => isAdminOrSuper ? setShowExportDialog(true) : exportFiltered()}>{exporting ? 'Exportando…' : 'Exportar vista CSV'}</button>
          {!isReadOnly && (
            <button onClick={recalculateAll} disabled={recalculating} className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1" data-testid="recalculate-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${recalculating ? 'animate-spin' : ''}`} /> {recalculating ? 'Calculando...' : 'Recalcular scores'}
            </button>
          )}
          <button className={`p-2 rounded ${viewMode === 'table' ? 'bg-blue-100 text-blue-600' : 'bg-white border border-slate-200'}`} onClick={() => setViewMode('table')} data-testid="view-table-btn">
            <LayoutList className="w-4 h-4" />
          </button>
          <button className={`p-2 rounded ${viewMode === 'cards' ? 'bg-blue-100 text-blue-600' : 'bg-white border border-slate-200'}`} onClick={() => setViewMode('cards')} data-testid="view-cards-btn">
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-end gap-3" data-testid="assets-geographic-toolbar">
        <div className="w-full sm:w-56"><GeographicFilter value={geoFilter} onChange={setGeoFilter} /></div>
        {canSeeServicer && <label className="text-xs w-full sm:w-56">SERVICER<select className="input-base mt-1" data-testid="assets-servicer-filter" value={servicerFilter} onChange={e => setServicerFilter(e.target.value)}><option value="">Todos</option><option value="__vacio__">Sin SERVICER asignado</option>{servicers.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}</select></label>}
        {isAdminOrSuper && <Link className="btn-secondary text-xs" data-testid="assets-manage-zones" to="/zonas-exclusion">Gestionar zonas de exclusión</Link>}
      </div>
      <div className="card-base p-4" style={{ overflow: 'visible' }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por NPL, referencia, dirección, municipio..." className="pl-10 input-base" data-testid="search-input" />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={hasActiveFilters ? 'border-blue-500 text-blue-600' : ''} data-testid="toggle-filters-btn">
            <Filter className="w-4 h-4 mr-2" />Filtros
            {hasActiveFilters && <span className="ml-2 w-2 h-2 bg-blue-600 rounded-full" />}
          </button>
          <button
            onClick={() => { setNewFilterName(''); setNewFilterIsDefault(false); setShowSaveFilterModal(true); }}
            disabled={!hasActiveFilters}
            className="btn-secondary text-sm flex items-center gap-1 disabled:opacity-40"
            title={hasActiveFilters ? 'Guardar este filtro como favorito' : 'Aplica al menos un filtro antes de guardar'}
            data-testid="save-filter-btn"
          >
            <Save className="w-3.5 h-3.5" /> Guardar filtro
          </button>
          <div className="relative" ref={colPickerRef}>
            <button
              type="button"
              onClick={() => setShowColPicker(s => !s)}
              className="btn-secondary text-sm flex items-center gap-1"
              title="Elige qué columnas se muestran en la tabla"
              data-testid="col-picker-btn"
            >
              <Filter className="w-3.5 h-3.5 rotate-90" /> Columnas
            </button>
            {showColPicker && (
              <div
                className="absolute right-0 mt-1 z-30 w-72 max-h-[480px] overflow-y-auto rounded-lg shadow-2xl p-3"
                style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                data-testid="col-picker-dropdown"
              >
                <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-muted)' }}>Columnas visibles</p>
                <div className="space-y-1">
                  {availableColumnDefs.map(c => (
                    <label key={c.key} className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:opacity-90" style={{ background: visibleColumns.includes(c.key) ? 'var(--color-bg-elevated)' : 'transparent' }}>
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(c.key)}
                        onChange={() => toggleColumn(c.key)}
                        disabled={c.fixed}
                        className="w-3.5 h-3.5"
                        data-testid={`col-toggle-${c.key}`}
                      />
                      <span className="text-sm">{c.label}{c.fixed ? <span className="text-[10px] ml-1 opacity-60">(fijo)</span> : null}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-2 pt-2 flex justify-between" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <button type="button" onClick={() => setVisibleColumns(DEFAULT_COLUMNS)} className="text-xs underline opacity-70 hover:opacity-100" data-testid="col-reset">Por defecto</button>
                  <button type="button" onClick={() => setVisibleColumns(availableColumnDefs.map(c => c.key))} className="text-xs underline opacity-70 hover:opacity-100" data-testid="col-select-all">Todas</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Saved filters chips bar */}
        {savedFiltersLoaded && savedFilters.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="saved-filters-bar">
            <span className="text-xs font-medium text-slate-500 mr-1">Mis filtros:</span>
            {savedFilters.map(f => {
              const isActive = activeSavedFilterId === f.id;
              return (
                <div
                  key={f.id}
                  className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full border text-xs font-medium transition-colors ${
                    isActive ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-400'
                  }`}
                  data-testid={`saved-filter-chip-${f.id}`}
                >
                  <button onClick={() => applySavedFilter(f)} className="flex items-center gap-1" title="Aplicar filtro">
                    {f.is_default && <Star className={`w-3 h-3 ${isActive ? 'fill-white' : 'fill-amber-400 text-amber-400'}`} />}
                    <span>{f.name}</span>
                  </button>
                  <button
                    onClick={() => toggleDefaultSavedFilter(f.id)}
                    className={`p-0.5 rounded-full hover:bg-black/10 ${isActive ? 'text-white' : (f.is_default ? 'text-amber-500' : 'text-slate-400 hover:text-amber-500')}`}
                    title={f.is_default
                      ? 'Predeterminado — click para desmarcar'
                      : 'Marcar como predeterminado (se aplicará al abrir Base de Activos)'}
                    data-testid={`saved-filter-default-${f.id}`}
                    aria-pressed={f.is_default}
                  >
                    <Star className={`w-3.5 h-3.5 ${f.is_default ? 'fill-amber-400 text-amber-500' : ''}`} />
                  </button>
                  <button
                    onClick={() => deleteSavedFilter(f.id)}
                    className={`p-0.5 rounded-full hover:bg-black/10 ${isActive ? 'text-white' : 'text-slate-400 hover:text-red-500'}`}
                    title="Eliminar filtro"
                    data-testid={`saved-filter-delete-${f.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 animate-fade-in pb-2">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <MultiSelect label="Tipo operativo" values={tipoOperativo} onChange={setTipoOperativo}
                options={[{v:'COMPRA_DIRECTA',l:'Compra Directa'},{v:'SUBASTA',l:'Subasta'},{v:'CREDITO',l:'Crédito'},{v:'REMATE',l:'Remate'},{v:'OCUPADO',l:'Ocupado'},{v:'REO',l:'REO'},{v:'sindatos',l:'Sin datos'}]} tid="filter-tipo-operativo" />
              <MultiSelect label="Tipo inmueble" values={tipoInmueble} onChange={setTipoInmueble}
                options={(filters?.tipos_inmueble && filters.tipos_inmueble.length
                  ? filters.tipos_inmueble
                  : ['vivienda','piso','casa','chalet','local','terreno','parking','trastero','edificio','nave','otro']
                ).map(t => ({v:t, l:t.charAt(0).toUpperCase() + t.slice(1)}))} tid="filter-tipo-inmueble" />
              <MultiSelect label="Estado" values={estado} onChange={setEstado}
                options={ESTADO_OPTIONS} tid="filter-estado" tooltip={TOOLTIP_ESTADO} />
              <MultiSelect label="Motivo cierre" values={motivoDescarte} onChange={setMotivoDescarte}
                options={CLOSURE_FILTER_OPTIONS} tid="filter-motivo-descarte" />
              <MultiSelect label="Fase" values={etapaVisual} onChange={setEtapaVisual}
                options={ETAPA_VISUAL_OPTIONS.map(v => ({ v, l: ETAPA_VISUAL_LABELS[v] }))} tid="filter-etapa-visual" tooltip={TOOLTIP_ETAPA} />
              <MultiSelect label="Posesion" values={posesion} onChange={setPosesion}
                options={[{v:'con_posesion',l:'Con posesion'},{v:'sin_posesion',l:'Sin posesion'},{v:'vacio',l:'Vacio'},{v:'no_consta',l:'No consta'}]} tid="filter-posesion" />
              <MultiSelect label="Semaforo" values={semaforo} onChange={setSemaforo}
                options={[{v:'verde',l:'Verde'},{v:'amarillo',l:'Amarillo'},{v:'naranja',l:'Naranja'},{v:'rojo',l:'Rojo'}]} tid="filter-semaforo" />
              <MultiSelect label="Acción Automática" values={accion} onChange={setAccion}
                options={[{v:'PRIORIDAD_ALTA',l:'Prioridad Alta'},{v:'REVISAR',l:'Revisar'},{v:'STANDBY',l:'Standby'},{v:'DESCARTAR',l:'Descartar'}]} tid="filter-accion" />
              <MultiSelect label="Fase judicial" values={faseJudicial} onChange={setFaseJudicial}
                options={[...FASE_JUDICIAL_OPTIONS.map(v => ({ v, l: FASE_JUDICIAL_LABELS[v] })), { v: 'sindatos', l: 'Sin datos' }]} tid="filter-fase-judicial" />
              <MultiSelect label="Provincia" values={provincia} onChange={setProvincia}
                options={(filters?.provincias || []).map(p => ({v:p,l:p}))} tid="filter-provincia" />
              {isAdminOrSuper && (
                <div className="flex flex-col gap-1" data-testid="filter-assigned-user-wrapper">
                  <label className="text-xs font-medium text-slate-600">Usuario asignado</label>
                  <select
                    value={assignedUser}
                    onChange={e => setAssignedUser(e.target.value)}
                    className="input-base text-xs"
                    data-testid="filter-assigned-user"
                    title="Muestra activos donde el usuario aparece como gestor, mediador o inversor"
                  >
                    <option value="">— cualquier usuario —</option>
                    {usersList.map(u => (
                      <option key={u.id} value={u.id}>{`${u.name || u.email} (${u.role})`}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-3 pt-3 border-t border-slate-100 flex-wrap">
              <label className="inline-flex items-center gap-2 text-xs text-slate-700 bg-slate-50 px-3 py-1.5 rounded-md border border-slate-200 cursor-pointer hover:bg-slate-100" data-testid="filter-sin-m2-wrapper">
                <input type="checkbox" checked={sinM2} onChange={e => setSinM2(e.target.checked)} className="w-4 h-4" data-testid="filter-sin-m2" />
                Sin m² (pendiente Catastro)
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 px-3 py-1.5 rounded-md border border-emerald-200 cursor-pointer hover:bg-emerald-100 font-medium" data-testid="filter-es-top-wrapper">
                <input type="checkbox" checked={esTop} onChange={e => setEsTop(e.target.checked)} className="w-4 h-4" data-testid="filter-es-top" />
                <Rocket className="w-3.5 h-3.5 fill-emerald-200" /> Top oportunidad (ROI ≥ 60%)
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-amber-800 bg-amber-50 px-3 py-1.5 rounded-md border border-amber-300 cursor-pointer hover:bg-amber-100 font-medium" data-testid="filter-pendiente-revision-wrapper">
                <input
                  type="checkbox"
                  checked={pendienteRevision}
                  onChange={e => setPendienteRevision(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="filter-pendiente-revision"
                />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" /> Solicitada revisión
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 px-3 py-1.5 rounded-md border border-emerald-300 cursor-pointer hover:bg-emerald-100 font-medium" data-testid="filter-nuevos-import-wrapper">
                <input
                  type="checkbox"
                  checked={esNuevoImport}
                  onChange={e => setEsNuevoImport(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="filter-nuevos-import"
                />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" /> Nuevos (última importación)
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-orange-800 bg-orange-50 px-3 py-1.5 rounded-md border border-orange-300 cursor-pointer hover:bg-orange-100 font-medium" data-testid="filter-en-descuento-wrapper">
                <input
                  type="checkbox"
                  checked={enDescuento}
                  onChange={e => setEnDescuento(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="filter-en-descuento"
                />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500" /> En descuento
              </label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="input-base text-xs w-48" data-testid="sort-by">
                <option value="score_premediacion">Score Económico</option>
                <option value="codigo_npl">Código NPL</option>
                <option value="updated_at">Ultima actualizacion</option>
                <option value="precio_compra">Precio compra</option>
                <option value="roi_rapido">ROI Venta</option>
                <option value="roi_alquiler">ROI Alquiler</option>
              </select>
              <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="input-base text-xs w-32" data-testid="sort-order">
                <option value="desc">Descendente</option>
                <option value="asc">Ascendente</option>
              </select>
              <button onClick={clearFilters} className="text-slate-500 text-xs flex items-center gap-1" data-testid="clear-filters-btn">
                <X className="w-3 h-3" /> Limpiar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk action bar — sticky at the top while the user scrolls the asset list. */}
      {selectedIds.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg shadow-md"
          style={{ position: 'sticky', top: 8, zIndex: 30 }}
          data-testid="bulk-bar"
        >
          <CheckSquare className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-slate-700">{selectedIds.length} seleccionados</span>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <button onClick={enrichSelectedCatastro} disabled={enrichingSel} className="btn-secondary text-sm flex items-center gap-1" data-testid="bulk-enrich-catastro-btn" title="Obtiene datos, m², latitud y longitud de Catastro para los activos seleccionados">
              {enrichingSel ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {enrichingSel ? 'Consultando Catastro...' : 'SOLICITAR DATOS A CATASTRO'}
            </button>
            <button onClick={recalcSelectedScore} disabled={recalcSel || isReadOnly} className={`btn-secondary text-sm flex items-center gap-1 ${isReadOnly ? 'hidden' : ''}`} data-testid="bulk-recalc-score-btn" title="Recalcular Score Económico para la selección">
              {recalcSel ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {recalcSel ? 'Recalculando...' : 'Calcular Score Económico'}
            </button>
            {isAdminOrSuper && (
              <button
                onClick={() => setShowBulkAssignModal(true)}
                disabled={selectedIds.length === 0}
                className="btn-secondary text-sm flex items-center gap-1"
                data-testid="bulk-assign-btn"
                title="Asignar los activos seleccionados a un usuario (elige la función)"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Asignar a
              </button>
            )}
            {isAdminOrSuper && (
              <button
                onClick={() => setShowBulkReassignModal(true)}
                disabled={selectedIds.length === 0}
                className="btn-secondary text-sm flex items-center gap-1"
                data-testid="bulk-reassign-btn"
                title="Añadir usuarios o quitar las asignaciones de una función"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Reasignar
              </button>
            )}
            <select value={bulkEstado} onChange={e => setBulkEstado(e.target.value)} className="input-base text-sm" data-testid="bulk-estado-select">
              <option value="">Cambiar estado a...</option>
              {ESTADO_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            <button onClick={applyBulkEstado} disabled={!bulkEstado || applyingBulk} className="btn-primary text-sm" data-testid="bulk-apply-btn">
              {applyingBulk ? 'Aplicando...' : 'Aplicar'}
            </button>
            <select value={bulkPrioridad} onChange={e => setBulkPrioridad(e.target.value)} className="input-base text-sm" data-testid="bulk-prioridad-select">
              <option value="">Cambiar prioridad a...</option>
              <option value="urgente">Urgente</option>
              <option value="alta">Alta</option>
              <option value="normal">Normal</option>
              <option value="baja">Baja</option>
              <option value="no_prioritario">No prioritario</option>
            </select>
            <button onClick={applyBulkPrioridad} disabled={!bulkPrioridad || applyingBulk} className="btn-primary text-sm" data-testid="bulk-apply-prioridad-btn">
              {applyingBulk ? 'Aplicando...' : 'Aplicar'}
            </button>
            <button onClick={clearSelection} className="text-slate-500 text-sm hover:text-slate-700" data-testid="bulk-clear-btn">Cancelar</button>
          </div>
          {/* Nota compartida opcional: se añade a la pestaña Notas de cada
              activo modificado por cualquier acción en bloque (estado,
              prioridad, asignación, descarte). */}
          <div className="w-full flex items-center gap-2 pt-1">
            <input
              type="text"
              value={bulkNota}
              onChange={e => setBulkNota(e.target.value)}
              placeholder="Nota opcional a añadir en la pestaña Notas de todos los activos seleccionados"
              className="input-base text-sm flex-1"
              maxLength={2000}
              data-testid="bulk-nota-input"
            />
            {bulkNota && (
              <button
                type="button"
                onClick={() => setBulkNota('')}
                className="text-slate-500 text-xs hover:text-slate-700"
                data-testid="bulk-nota-clear"
                title="Limpiar la nota"
              >
                Limpiar nota
              </button>
            )}
          </div>
        </div>
      )}

      {loading && <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>}

      {!loading && assets.length === 0 && (
        <div className="card-base p-12 text-center">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="heading-3 mb-2">No hay activos</h3>
          <p className="body-text mb-4">{hasActiveFilters ? 'No se encontraron activos con los filtros' : 'Importa tu primer listado'}</p>
          {hasActiveFilters ? <button onClick={clearFilters}>Limpiar filtros</button> : <Link to="/importar"><button className="btn-primary">Importar Excel</button></Link>}
        </div>
      )}

      {/* Table — internal vertical scroll (fixed tall viewport) + sticky header so the
           horizontal scrollbar sits right below the visible rows and remains reachable
           while the user scrolls the page vertically. */}
      {!loading && assets.length > 0 && viewMode === 'table' && (
        <div
          className="table-wrapper"
          style={{
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 260px)',
            minHeight: '400px',
          }}
          data-testid="assets-table-wrapper"
        >
          <table style={{ minWidth: '1200px', borderCollapse: 'collapse', width: '100%' }} data-testid="assets-table">
            <thead style={{ position: 'sticky', top: 0, zIndex: 5, background: '#f8fafc' }}>
              <tr>
                <th className="table-header text-center" style={{ width: '32px', background: '#f8fafc' }}>
                  <input type="checkbox" checked={allPageSelected} onChange={togglePageSelection} className="w-4 h-4 rounded border-slate-300" data-testid="bulk-select-all" />
                </th>
                <th className="table-header" style={{ width: '28px', background: '#f8fafc' }}></th>
                {availableColumnDefs.filter(c => colVisible(c.key)).map(c => (
                  <th
                    key={c.key}
                    className={`table-header ${['economico', 'roi_alquiler'].includes(c.key) ? 'text-center' : ''}`}
                    style={{ width: `${c.width}px`, background: '#f8fafc' }}
                    data-testid={`col-header-${c.key}`}
                    title={c.key === 'estado' ? TOOLTIP_ESTADO : (c.key === 'etapa_visual' ? TOOLTIP_ETAPA : undefined)}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assets.map(a => {
                const valorIndividual = a.valor_salida_base;
                const valorBase = a.valor_salida_base;
                const hasIndividual = !!a.valoracion_resultado && valorIndividual && valorIndividual > 0;
                const hasBase = valorBase && valorBase > 0;
                const checked = selectedIds.includes(a.id);

                const renderCell = (key) => {
                  switch (key) {
                    case 'npl':
                      return (
                        <span className="inline-flex items-center gap-1 flex-wrap whitespace-nowrap">
                          <span className="text-xs font-semibold bg-slate-900 text-white px-1.5 py-0.5 rounded" data-testid={`asset-npl-${a.id}`}>{a.codigo_npl || '-'}</span>
                          <GeographicBadge geo={a.exclusion_geografica} id={`table-${a.id}`} />
                          {a.marked_for_review && (
                            <span
                              className="text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300 px-1 py-0.5 rounded tracking-wide"
                              title="Este activo tiene revisión solicitada"
                              data-testid={`asset-review-badge-${a.id}`}
                            >
                              SOLICITADA
                            </span>
                          )}
                          {a.es_nuevo_import && (
                            <span
                              className="text-[9px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 px-1 py-0.5 rounded tracking-wide"
                              title={a.fecha_primera_importacion
                                ? `Nuevo: añadido el ${new Date(a.fecha_primera_importacion).toLocaleDateString('es-ES')}`
                                : 'Nuevo: añadido en la última importación'}
                              data-testid={`asset-nuevo-import-badge-${a.id}`}
                            >
                              NUEVO
                            </span>
                          )}
                          {typeof a.descuento_pct === 'number' && a.descuento_pct < 0 && (
                            <span
                              className="text-[9px] font-bold bg-orange-100 text-orange-800 border border-orange-300 px-1 py-0.5 rounded tracking-wide"
                              title={a.precio_anterior_importacion && a.precio_actual_importacion
                                ? `Bajada de precio (${a.precio_campo_importacion || '—'}): ${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(a.precio_anterior_importacion)} → ${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(a.precio_actual_importacion)}`
                                : 'Bajada de precio detectada en la última importación'}
                              data-testid={`asset-descuento-badge-${a.id}`}
                            >
                              {`${a.descuento_pct}%`}
                            </span>
                          )}
                        </span>
                      );
                    case 'referencia':
                      return (
                        <Link to={`/activos/${a.id}`} className="text-blue-600 hover:text-blue-700 font-medium text-sm whitespace-nowrap" title={a.referencia_catastral || 'Sin referencia catastral'} data-testid={`asset-link-${a.id}`}>
                          {a.referencia_catastral || <span className="text-slate-400 font-normal">—</span>}
                        </Link>
                      );
                    case 'direccion':
                      return <span className="text-sm" title={a.direccion || ''}>{a.direccion || '-'}</span>;
                    case 'municipio':
                      return <span className="text-sm" title={a.municipio || ''}>{a.municipio || '-'}</span>;
                    case 'tipo_op':
                      return <span className="text-xs text-slate-500">{a.tipo_activo_operativo || '-'}</span>;
                    case 'precio':
                      return <span className="text-sm font-medium whitespace-nowrap" title={a.precio_compra_es_estimado ? 'Precio estimado automáticamente' : ''}>{formatCurrency(a.precio_compra)}{a.precio_compra_es_estimado ? ' *' : ''}</span>;
                    case 'valor':
                      return hasIndividual
                        ? <span className="font-bold text-slate-800 inline-flex items-center gap-1 whitespace-nowrap" title="Valor salida (individualizado)">{formatCurrency(valorIndividual)}<span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-1 rounded">ind.</span></span>
                        : hasBase
                          ? <span className="italic text-slate-500 whitespace-nowrap" title="Valor salida base (m2 x precio zona)">{formatCurrency(valorBase)}</span>
                          : <span className="text-slate-300">-</span>;
                    case 'economico': {
                      // Score económico = ROI Venta. Si existe un Análisis
                      // ROI calculado (marker: tipo_calculo_aplicado) se
                      // muestra `roi_total` como "Real"; si no, `roi_rapido`
                      // como "Estimado". Regla común en toda la app.
                      const value = getRoiVentaValue(a);
                      return (
                        <RoiValue
                          value={value}
                          isReal={hasRealRoiVenta(a)}
                          kind="venta"
                          size="sm"
                          testid={`score-total-${a.id}`}
                        />
                      );
                    }
                    case 'roi_alquiler': {
                      // ROI de alquiler — "Real" cuando el usuario ha
                      // introducido la renta manualmente (applied_rent_source
                      // === 'manual'); "Estimado" cuando el importe procede
                      // del cálculo automático Fotocasa.
                      const roiA = getRoiAlquilerValue(a);
                      return (
                        <RoiValue
                          value={roiA}
                          isReal={hasRealRoiAlquiler(a)}
                          kind="alquiler"
                          size="sm"
                          testid={`roi-alquiler-${a.id}`}
                        />
                      );
                    }
                    case 'accion':
                      return a.accion_recomendada ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 whitespace-nowrap ${ACCION_BADGES[a.accion_recomendada] || 'bg-slate-100 text-slate-600'}`} data-testid="accion-badge">
                          {a.es_top && <Rocket className="w-3 h-3 fill-emerald-200" title="Top oportunidad (ROI ≥ 60%)" />}
                          {a.accion_recomendada.replace('_', ' ')}
                        </span>
                      ) : <span className="text-slate-300">-</span>;
                    case 'etapa_visual': {
                      // Etapa editable — el usuario puede moverla libremente
                      // entre los 6 valores del pipeline (sin orden obligatorio).
                      // Bloqueada cuando estado=cerrado (spec Feb 2026 §4).
                      // Mediador: sólo lectura + click abre modal Solicitar revisión.
                      const ev = a.etapa_visual;
                      const lockedByCerrado = a.estado === 'cerrado';
                      const isDisabled = lockedByCerrado || isMediador;
                      const disabledTitle = isMediador
                        ? 'Solicita el cambio al Gestor o Administrador'
                        : (lockedByCerrado ? 'Fase bloqueada: el activo está CERRADO' : undefined);
                      const onDisabledClick = isMediador
                        ? ((asset) => { setMediadorRoAsset(asset); setMediadorRoField('fase'); })
                        : undefined;
                      // Interceptor: paso a "mediacion" abre modal de
                      // asignación obligatoria de mediador (spec Feb 2026).
                      // Paso a "oferta" / "proceso_compra" pregunta si el
                      // activo debe seguir en mediación activa.
                      const beforeSave = (newVal, asset) => {
                        if (newVal === 'mediacion') {
                          setMediacionAsset(asset);
                          return true;
                        }
                        if (newVal === 'oferta' || newVal === 'proceso_compra') {
                          setConfirmMediacionActiva({ asset, value: newVal });
                          return true;
                        }
                        return false;
                      };
                      if (!ev) {
                        return (
                          <InlineChip
                            asset={a}
                            field="etapa_visual"
                            options={ETAPA_VISUAL_OPTIONS.map(v => ({ v, l: ETAPA_VISUAL_LABELS[v] }))}
                            currentValue={null}
                            currentLabel={'—'}
                            badgeClass="bg-slate-100 text-slate-600 border-slate-200"
                            testid={`chip-etapa-${a.id}`}
                            disabled={isDisabled}
                            disabledTitle={disabledTitle}
                            onDisabledClick={onDisabledClick}
                            beforeSave={beforeSave}
                          />
                        );
                      }
                      return (
                        <div className="flex items-center gap-1 flex-wrap">
                          <InlineChip
                            asset={a}
                            field="etapa_visual"
                            options={ETAPA_VISUAL_OPTIONS.map(v => ({ v, l: ETAPA_VISUAL_LABELS[v] }))}
                            currentValue={ev}
                            currentLabel={ETAPA_VISUAL_LABELS[ev] || ev}
                            badgeClass={ETAPA_VISUAL_BADGES[ev] || 'bg-slate-100 text-slate-600 border-slate-200'}
                            testid={`chip-etapa-${a.id}`}
                            disabled={isDisabled}
                            disabledTitle={disabledTitle}
                            onDisabledClick={onDisabledClick}
                            beforeSave={beforeSave}
                          />
                          {a.mediacion_activa && (ev === 'oferta' || ev === 'proceso_compra') && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${ETAPA_VISUAL_BADGES['mediacion']}`}
                              title="Mediación activa"
                              data-testid={`chip-etapa-mediacion-extra-${a.id}`}
                            >
                              MEDIACIÓN
                            </span>
                          )}
                        </div>
                      );
                    }
                    case 'estado':
                      return (
                        <div className="flex flex-col items-start gap-0.5">
                          <InlineChip
                            asset={a}
                            field="estado"
                            options={ESTADO_OPTIONS}
                            currentValue={a.estado || 'nuevo'}
                            currentLabel={(ESTADO_OPTIONS.find(o => o.v === (a.estado || 'nuevo'))?.l) || a.estado || 'nuevo'}
                            badgeClass={
                              a.estado === 'descartado' ? 'bg-red-100 text-red-700 border-red-200'
                              : a.estado === 'activo' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                              : a.estado === 'revisar' || a.estado === 'en_proceso' ? 'bg-amber-100 text-amber-700 border-amber-200'
                              : a.estado === 'standby' ? 'bg-slate-200 text-slate-600 border-slate-300'
                              : a.estado === 'cerrado' || a.estado === 'papelera' ? 'bg-slate-100 text-slate-500 border-slate-200'
                              : 'bg-blue-100 text-blue-700 border-blue-200'
                            }
                            testid={`chip-estado-${a.id}`}
                            disabled={isMediador}
                            disabledTitle={isMediador ? 'Solicita el cambio al Gestor o Administrador' : undefined}
                            onDisabledClick={isMediador ? ((asset) => { setMediadorRoAsset(asset); setMediadorRoField('estado'); }) : undefined}
                            beforeSave={(newVal, asset) => {
                              // (Feb 2026) Pasar a "cerrado" desde la tabla
                              // exige elegir motivo. Abre el modal específico
                              // por-fila sin tocar la selección bulk.
                              if (newVal === 'cerrado') {
                                setIndividualClosureId(asset.id);
                                return true;
                              }
                              return false;
                            }}
                          />
                          {a.estado === 'descartado' && a.descarte_motivos && a.descarte_motivos.length > 0 && (
                            <span
                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded border"
                              style={{ background: 'color-mix(in oklab, #ef4444 18%, transparent)', borderColor: 'color-mix(in oklab, #ef4444 45%, transparent)', color: '#ef4444' }}
                              title={a.descarte_motivos.map(v => DESCARTE_LABEL[v] || v).join(', ')}
                              data-testid={`descarte-chip-${a.id}`}
                            >
                              {DESCARTE_SHORT[a.descarte_motivos[0]] || a.descarte_motivos[0]}
                            </span>
                          )}
                        </div>
                      );
                    case 'fase_judicial':
                      // Fase judicial sólo aplica a activos de tipo CREDITO
                      // (cesión de crédito → procedimiento judicial). Para otros
                      // tipos operativos se muestra "—" con tooltip explicativo.
                      if (a.tipo_activo_operativo !== 'CREDITO') {
                        return (
                          <span
                            className="text-xs text-slate-300"
                            title="Fase judicial sólo aplica a activos de tipo Crédito"
                            data-testid={`fj-na-${a.id}`}
                          >—</span>
                        );
                      }
                      return (
                        <InlineChip
                          asset={a}
                          field="fase_judicial"
                          options={FASE_JUDICIAL_OPTIONS.map(v => ({ v, l: FASE_JUDICIAL_LABELS[v] }))}
                          currentValue={a.fase_judicial}
                          currentLabel={a.fase_judicial ? (FASE_JUDICIAL_LABELS[a.fase_judicial] || a.fase_judicial) : null}
                          badgeClass="bg-purple-50 text-purple-700 border-purple-200"
                          placeholder="— sin fase judicial —"
                          testid={`chip-fase-judicial-${a.id}`}
                        />
                      );
                    case 'prioridad':
                      return (
                        <InlineChip
                          asset={a}
                          field="prioridad"
                          options={[
                            { v: 'urgente', l: 'Urgente' },
                            { v: 'alta', l: 'Alta' },
                            { v: 'normal', l: 'Normal' },
                            { v: 'baja', l: 'Baja' },
                            { v: 'no_prioritario', l: 'No prioritario' },
                          ]}
                          currentValue={a.prioridad}
                          currentLabel={PRIORIDAD_LABEL[a.prioridad] || a.prioridad || null}
                          badgeClass={getPrioridadBadge(a.prioridad)}
                          placeholder="— sin prioridad —"
                          testid={`chip-prioridad-${a.id}`}
                        />
                      );
                    case 'ocupacion':
                      return <span className="text-xs text-slate-600">{a.posesion ? a.posesion.replace(/_/g, ' ') : '-'}</span>;
                    case 'gestor':
                    case 'inversor':
                      return <AssetAssignmentCell asset={a} funcion={key} directory={usersList} />;
                    case 'mediador': {
                      // (Feb 2026) Columna Mediador — botón que abre el
                      // modal por-fila para asignar/desasignar mediadores.
                      // Muestra los nombres separados por coma o queda vacío.
                      const ids = a.mediadores || [];
                      const names = assignedNames(a, 'mediadores', usersList);
                      const canEdit = isAdminOrSuper;
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!canEdit) return;
                            setMediadoresEditAsset(a);
                            setMediadoresDraft(ids);
                          }}
                          className={`text-xs px-2 py-1 rounded border truncate max-w-[140px] text-left ${
                            names.length > 0
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                              : 'bg-slate-50 text-slate-400 border-dashed border-slate-300 hover:bg-slate-100'
                          } ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
                          title={names.length > 0 ? names.join(', ') : (canEdit ? 'Click para asignar mediadores' : 'Sin mediadores')}
                          disabled={!canEdit}
                          data-testid={`chip-mediador-${a.id}`}
                        >
                          {names.length > 0 ? names.join(', ') : (canEdit ? '+ Asignar' : '—')}
                        </button>
                      );
                    }
                    case 'servicer': {
                      const name = a.servicer_id ? (servicerNames.get(a.servicer_id) || 'SERVICER no disponible') : 'Sin SERVICER';
                      return <span className="text-xs block max-w-[120px] whitespace-normal [overflow-wrap:anywhere]" title={name} data-testid={`asset-servicer-${a.id}`}>{name}</span>;
                    }
                    case 'proveedor':
                      return <span className="text-xs text-slate-600 truncate inline-block max-w-[110px]" title={a.proveedor || ''}>{a.proveedor || '-'}</span>;
                    case 'provincia':
                      return <span className="text-sm">{a.provincia || '-'}</span>;
                    case 'tipo_activo':
                      return <span className="text-xs text-slate-600">{a.tipo_activo || '-'}</span>;
                    case 'metros':
                      return <span className="text-sm whitespace-nowrap">{a.metros ? `${a.metros} m²` : '-'}</span>;
                    case 'updated_at':
                      return <span className="text-xs text-slate-500 whitespace-nowrap">{formatDateTime(a.updated_at)}</span>;
                    default:
                      return null;
                  }
                };

                return (
                  <tr
                    key={a.id}
                    className={`table-row ${checked ? 'bg-blue-50' : ''}`}
                    style={a.estado === 'descartado' ? { background: 'color-mix(in oklab, #ef4444 7%, transparent)' } : undefined}
                    data-testid={`asset-row-${a.id}`}
                  >
                    <td className="table-cell text-center">
                      <input type="checkbox" checked={checked} onChange={() => toggleOne(a.id)} className="w-4 h-4 rounded border-slate-300" data-testid={`bulk-check-${a.id}`} />
                    </td>
                    <td className="table-cell"><SemaforoDot value={a.semaforo} /></td>
                    {availableColumnDefs.filter(c => colVisible(c.key)).map(c => (
                      <td
                        key={c.key}
                        className={`table-cell ${(c.key === 'economico' || c.key === 'roi_alquiler') ? 'text-center' : ''}`}
                        style={{ maxWidth: `${c.width + 20}px`, overflow: ['direccion','municipio','referencia','tipo_op','tipo_activo','proveedor'].includes(c.key) ? 'hidden' : undefined, textOverflow: ['direccion','municipio','referencia','tipo_op','tipo_activo','proveedor'].includes(c.key) ? 'ellipsis' : undefined, whiteSpace: ['direccion','municipio','referencia','tipo_op','tipo_activo','proveedor'].includes(c.key) ? 'nowrap' : undefined }}
                      >
                        {renderCell(c.key)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Cards */}
      {!loading && assets.length > 0 && viewMode === 'cards' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assets-cards">
          {assets.map(a => (
            <Link key={a.id} to={`/activos/${a.id}`} className="card-base min-w-0 p-5 hover:shadow-lg transition-shadow" data-testid={`asset-card-${a.id}`}>
              <GeographicBadge geo={a.exclusion_geografica} id={`card-${a.id}`} />
              <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <SemaforoDot value={a.semaforo} />
                  {a.codigo_npl && <span className="text-[10px] font-bold bg-slate-900 text-white px-1.5 py-0.5 rounded">{a.codigo_npl}</span>}
                  {getRoiVentaValue(a) != null && (
                    <span className="flex items-center gap-1">
                      {a.es_top && <Rocket className="w-4 h-4 fill-emerald-100" />}
                      <RoiValue
                        value={getRoiVentaValue(a)}
                        isReal={hasRealRoiVenta(a)}
                        kind="venta"
                        size="lg"
                        testid={`card-roi-venta-${a.id}`}
                      />
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 items-center min-w-0">
                  <span className={getEstadoBadge(a.estado)}>{a.estado || 'nuevo'}</span>
                  {a.accion_recomendada && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ACCION_BADGES[a.accion_recomendada] || ''}`}>{a.accion_recomendada.replace('_',' ')}</span>}
                </div>
              </div>
              <h3 className="font-medium text-slate-900 mb-1 truncate">{a.direccion || a.referencia_catastral || 'Sin dirección'}</h3>
              <p className="text-sm text-slate-500 mb-3">{a.municipio}{a.provincia && `, ${a.provincia}`}</p>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                <div>
                  <p className="text-xs text-slate-400">Precio{a.precio_compra_es_estimado ? ' *' : ''}</p>
                  <p className="font-medium text-slate-900 text-sm" title={a.precio_compra_es_estimado ? 'Precio estimado automáticamente' : ''}>{formatCurrency(a.precio_compra)}{a.precio_compra_es_estimado ? ' *' : ''}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Tipo</p>
                  <p className="text-sm text-slate-600">{a.tipo_activo_operativo || '-'}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && total > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="pagination">
          <p className="text-sm text-slate-500">Mostrando <span className="font-medium text-slate-700">{from}-{to}</span> de <span className="font-medium text-slate-700">{total}</span></p>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-slate-500 flex items-center gap-2">
              Por página
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                className="input-base text-xs w-20"
                data-testid="page-size-select"
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="btn-secondary text-sm flex items-center gap-1 disabled:opacity-40" data-testid="page-prev">
              <ChevronLeft className="w-3.5 h-3.5" /> Anterior
            </button>
            <span className="text-sm text-slate-600 px-2" data-testid="page-indicator">Página {page + 1} de {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page + 1 >= totalPages} className="btn-secondary text-sm flex items-center gap-1 disabled:opacity-40" data-testid="page-next">
              Siguiente <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      <CatastroBatchModal state={catastroBatch} onClose={() => setCatastroBatch(null)} />
      {/* Descarte motivos modal */}
      {showDescarteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="descarte-modal">
          <div className="rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Motivo de cierre</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>{selectedIds.length} activos pasarán a <span className="font-medium text-slate-400">Cerrado</span>. Selecciona el motivo principal.</p>
              </div>
              <button onClick={() => setShowDescarteModal(false)} className="hover:opacity-80" style={{ color: 'var(--color-text-muted)' }} data-testid="descarte-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2">
              {DESCARTE_MOTIVOS.map(m => (
                <label key={m.v} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:opacity-90" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }} data-testid={`descarte-motivo-${m.v}`}>
                  <input
                    type="radio"
                    name="bulk-descarte-motivo"
                    className="w-4 h-4"
                    checked={descarteMotivos.includes(m.v)}
                    onChange={() => toggleMotivo(m.v)}
                  />
                  <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{m.l}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowDescarteModal(false)} className="btn-secondary text-sm" data-testid="descarte-cancel-btn">Cancelar</button>
              <button onClick={confirmDescarte} disabled={applyingBulk || !descarteMotivos.length} className="btn-primary text-sm" data-testid="descarte-confirm-btn">
                {applyingBulk ? 'Aplicando...' : 'Confirmar cierre'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Mediador — informativo + Solicitar revisión (spec Feb 2026).
          Se abre al pulsar los chips Estado/Fase del listado. */}
      {mediadorRoAsset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="mediador-readonly-modal">
          <div className="rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}>
            <div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Campo no editable</h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Como <b>Mediador</b>, no puedes modificar directamente el campo <b>{mediadorRoField === 'estado' ? 'ESTADO' : 'FASE'}</b>.
                Solicita el cambio al <b>Gestor</b> o <b>Administrador</b> asignado.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => { setMediadorRoAsset(null); setMediadorRoField(null); }} className="btn-secondary text-sm" data-testid="mediador-readonly-cancel">
                Cerrar
              </button>
              <button
                onClick={() => { setShowBulkReview(true); }}
                className="btn-primary text-sm"
                data-testid="mediador-readonly-request-review"
              >
                Solicitar revisión
              </button>
            </div>
          </div>
        </div>
      )}
      <RequestReviewModal
        open={showBulkReview}
        onClose={() => { setShowBulkReview(false); setMediadorRoAsset(null); setMediadorRoField(null); }}
        asset={mediadorRoAsset}
        onSuccess={() => { setShowBulkReview(false); setMediadorRoAsset(null); setMediadorRoField(null); fetchAssets(); }}
      />

      <MediacionAssignModal
        open={!!mediacionAsset}
        asset={mediacionAsset}
        onClose={() => setMediacionAsset(null)}
        onConfirmed={() => { setMediacionAsset(null); fetchAssets(); }}
      />

      <ConfirmMediacionActivaModal
        open={!!confirmMediacionActiva}
        targetFaseLabel={confirmMediacionActiva?.value === 'oferta' ? 'Oferta' : 'Proceso compra'}
        onClose={() => setConfirmMediacionActiva(null)}
        onAnswer={async (activa) => {
          const { asset, value } = confirmMediacionActiva || {};
          setConfirmMediacionActiva(null);
          if (!asset) return;
          try {
            await axios.patch(
              `${API}/assets/${asset.id}/quick-update`,
              { etapa_visual: value, mediacion_activa: activa },
              { withCredentials: true },
            );
            toast.success('Fase actualizada' + (activa ? ' · mediación activa' : ''));
            fetchAssets();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
          }
        }}
      />

      {/* Modal por-fila "Motivo de cierre" al pasar estado a cerrado desde
          el chip de la tabla. Independiente del bulk (spec Feb 2026). */}
      <ClosureReasonModal
        open={!!individualClosureId}
        onClose={() => setIndividualClosureId(null)}
        onConfirm={async (motivos) => {
          const id = individualClosureId;
          setIndividualClosureId(null);
          if (!id) return;
          try {
            await axios.patch(
              `${API}/assets/${id}/quick-update`,
              { estado: 'cerrado', descarte_motivos: motivos },
              { withCredentials: true },
            );
            toast.success('Activo cerrado');
            fetchAssets();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
          }
        }}
      />

      {/* (Feb 2026) Modal por-fila para asignar/desasignar mediadores.
          Se abre desde la columna "Mediador" de la tabla. */}
      {isAdminOrSuper && mediadoresEditAsset && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={() => setMediadoresEditAsset(null)}
          data-testid="mediadores-edit-overlay"
        >
          <div
            className="rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col bg-white"
            onClick={e => e.stopPropagation()}
            data-testid="mediadores-edit-modal"
          >
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div>
                <h3 className="font-semibold text-slate-800">Mediadores asignados</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {mediadoresEditAsset.codigo_npl || mediadoresEditAsset.direccion || 'Activo'}
                </p>
              </div>
              <button
                onClick={() => setMediadoresEditAsset(null)}
                className="p-1 hover:bg-slate-100 rounded"
                data-testid="mediadores-edit-close"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-1">
              {usersList.map(u => {
                const checked = mediadoresDraft.includes(u.id);
                return (
                  <label
                    key={u.id}
                    className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1.5 rounded"
                    data-testid={`mediadores-edit-user-${u.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setMediadoresDraft(prev =>
                        prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id]
                      )}
                      className="w-4 h-4 accent-emerald-500"
                    />
                    <span className="text-slate-800">{u.name || u.email}</span>
                    <span className="text-xs text-slate-400">({u.role})</span>
                  </label>
                );
              })}
              {usersList.length === 0 && (
                <p className="text-sm italic text-slate-400 p-2">No hay usuarios disponibles.</p>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                onClick={() => setMediadoresEditAsset(null)}
                className="px-4 py-2 text-sm font-medium rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="mediadores-edit-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  const target = mediadoresEditAsset;
                  const ids = mediadoresDraft;
                  setMediadoresEditAsset(null);
                  try {
                    await axios.patch(
                      `${API}/assets/${target.id}/quick-update`,
                      { mediadores: ids },
                      { withCredentials: true },
                    );
                    toast.success(ids.length > 0 ? 'Mediadores actualizados' : 'Mediadores eliminados');
                    fetchAssets();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Error al guardar');
                  }
                }}
                className="px-4 py-2 text-sm font-semibold rounded bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="mediadores-edit-save"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save-filter modal */}
      {showSaveFilterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="save-filter-modal">
          <div className="rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Guardar filtro</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  Dale un nombre al filtro actual. Lo verás como chip en la parte superior para aplicarlo con un clic.
                </p>
              </div>
              <button onClick={() => setShowSaveFilterModal(false)} className="hover:opacity-80" style={{ color: 'var(--color-text-muted)' }} data-testid="save-filter-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>Nombre</label>
                <input
                  type="text"
                  className="input-base"
                  value={newFilterName}
                  onChange={e => setNewFilterName(e.target.value)}
                  placeholder="Ej: Alicante · Revisar · No descartado"
                  data-testid="save-filter-name-input"
                  autoFocus
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--color-text-secondary)' }} data-testid="save-filter-default-wrap">
                <input
                  type="checkbox"
                  checked={newFilterIsDefault}
                  onChange={e => setNewFilterIsDefault(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="save-filter-default-check"
                />
                Establecer como vista predeterminada al abrir Base de Activos
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowSaveFilterModal(false)} className="btn-secondary text-sm" data-testid="save-filter-cancel-btn">Cancelar</button>
              <button onClick={saveCurrentFilter} disabled={savingFilter || !newFilterName.trim()} className="btn-primary text-sm" data-testid="save-filter-confirm-btn">
                {savingFilter ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk assign modal — admin & superadmin. La FUNCIÓN (Gestor /
          Mediador / Inversor) se elige dentro del modal y define el
          vínculo con el activo, sin cambiar el rol global del usuario. */}
      {isAdminOrSuper && (
        <BulkAssignModal
          open={showBulkAssignModal}
          onClose={() => setShowBulkAssignModal(false)}
          assetIds={selectedIds}
          initialNote={bulkNota}
          onDone={() => { setShowBulkAssignModal(false); setSelectedIds([]); setBulkNota(''); fetchAssets(); }}
        />
      )}
      {isAdminOrSuper && (
        <BulkAssignModal
          open={showBulkReassignModal}
          allowClear
          onClose={() => setShowBulkReassignModal(false)}
          assetIds={selectedIds}
          initialNote={bulkNota}
          onDone={() => { setShowBulkReassignModal(false); setSelectedIds([]); setBulkNota(''); fetchAssets(); }}
        />
      )}
    </div>
  );
};

const MultiSelect = ({ label, values, onChange, options, tid, tooltip }) => {
  const [open, setOpen] = React.useState(false);
  const [menuLeft, setMenuLeft] = React.useState(0);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const position = () => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(336, window.innerWidth - 32);
      setMenuLeft(Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)) - rect.left);
    };
    position();
    window.addEventListener('resize', position);
    return () => window.removeEventListener('resize', position);
  }, [open]);

  const toggle = (v) => {
    onChange(values.includes(v) ? values.filter(x => x !== v) : [...values, v]);
  };

  const count = values.length;

  return (
    <div className="relative" ref={ref} data-testid={tid}>
      <button
        data-testid={`${tid}-trigger`}
        onClick={() => setOpen(!open)}
        title={tooltip || undefined}
        className={`input-base text-xs text-left flex items-center justify-between gap-1 ${count > 0 ? 'border-blue-400 bg-blue-50/50' : ''}`}
      >
        <span className="truncate">
          {count === 0 ? label : count === 1 ? options.find(o => o.v === values[0])?.l || values[0] : `${label} (${count})`}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div data-testid={`${tid}-menu`} className="absolute z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-y-auto"
          style={{ left: menuLeft, maxHeight: '220px', width: 'min(21rem, calc(100vw - 2rem))' }}>
          {options.map(o => (
            <label key={o.v} data-testid={`${tid}-option-${String(o.v).toLowerCase().replace(/[\s_]+/g, '-')}`} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-xs text-slate-700 break-words">
              <input type="checkbox" data-testid={`${tid}-checkbox-${String(o.v).toLowerCase().replace(/[\s_]+/g, '-')}`} checked={values.includes(o.v)} onChange={() => toggle(o.v)} className="w-3.5 h-3.5 shrink-0 rounded border-slate-300" />
              {o.l}
            </label>
          ))}
          {count > 0 && (
            <button data-testid={`${tid}-clear`} onClick={() => onChange([])} className="w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:text-red-500 border-t border-slate-100 mt-1">
              Limpiar seleccion
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AssetsPage;
